import { Box, CircularProgress, Grid, MenuItem, Select } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import inventoryExportImg from 'img/inventory-export.png'
import {
  createInventoryCsvReport,
  downloadInventoryCsvReport,
  fetchInventoryCsvReports,
  getStatusInventoryCsvReport,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import { CsvReportHistory, gameMetaData, normalizeCsvReportHistory } from 'shared'
import './exports-presenter.css'

export const InventoryExports = (): JSX.Element => {
  const { t } = useTranslation()
  const [idGame, setIdGame] = useState<string>('')
  const { enqueueSnackbar } = useSnackbar()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)

  const [downloads, setDownloads] = useState<CsvReportHistory[]>([])
  const [loadingReports, setLoadingReports] = useState(true)
  const [filterMonth, setFilterMonth] = useState<Date | null>(null)
  const [hasRecords, setHasRecords] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const handleCreateCsv = async () => {
    setIsCreating(true)
    try {
      const exportJob = await createInventoryCsvReport(idGame || undefined)
      const normalize = normalizeCsvReportHistory(exportJob)

      pollExportStatus(normalize.id)

      setDownloads((prev) => [normalize, ...prev])
      setHasRecords(true)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCreateExport'), { variant: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleFetchReports = async (overrideMonth?: Date | null) => {
    setLoadingReports(true)
    const month = overrideMonth !== undefined ? overrideMonth : filterMonth
    try {
      let createdAtFrom: string | undefined
      let createdAtTo: string | undefined
      if (month) {
        const year = month.getFullYear()
        const m = month.getMonth() + 1
        const monthStr = `${year}-${String(m).padStart(2, '0')}`
        createdAtFrom = `${monthStr}-01`
        const lastDay = new Date(year, m, 0).getDate()
        createdAtTo = `${monthStr}-${String(lastDay).padStart(2, '0')}`
      }
      const { files, hasNextPage } = await fetchInventoryCsvReports(
        currentPage,
        createdAtFrom,
        createdAtTo
      )
      if (files) {
        setDownloads(files)
        setIsLastPage(!hasNextPage)
        if (!month && files.length > 0) setHasRecords(true)
      } else {
        enqueueSnackbar(t('exports.errors.errorFetchReports'), { variant: 'error' })
      }
    } finally {
      setLoadingReports(false)
    }
  }

  const pollExportStatus = async (id: number) => {
    let res: any
    try {
      res = await getStatusInventoryCsvReport(id)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
      return
    }

    if (res.status === 'ready') {
      setDownloads((prev) => prev.map((exp) => (exp.id === id ? { ...exp, ...res.data } : exp)))
      enqueueSnackbar(t('exports.csvReady'), { variant: 'success' })
      handleFetchReports()
      return
    }

    if (res.status === 'pending') {
      setTimeout(() => pollExportStatus(id), 5000)
      return
    }

    enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
  }

  const today = new Date()

  useEffect(() => {
    handleFetchReports()
  }, [currentPage])

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      handleFetchReports()
    } else {
      setCurrentPage(1)
    }
  }

  const handleResetFilter = () => {
    setFilterMonth(null)
    if (currentPage === 1) {
      handleFetchReports(null)
    } else {
      setCurrentPage(1)
    }
  }

  const handleDownloadCsv = async (id: number, idGame?: string) => {
    let res
    try {
      res = await downloadInventoryCsvReport(id)
    } catch {
      enqueueSnackbar(t('exports.errors.errorDownloadCsv'), { variant: 'error' })
      return
    }

    const game = idGame ? gameMetaData.find((g) => g.idGame.toString() === idGame.toString()) : null
    const gameSuffix = game ? `-${game.name}` : ''

    const blob = new Blob([res.data], { type: 'text/csv' })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory-report${gameSuffix}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className, rowData }) => {
    rowData

    return (
      <div key={key} style={style} className={`${className}`}>
        {columns}
      </div>
    )
  }

  return (
    <div className="tab-page-content">
      {loadingReports ? (
        <div className="w-100 d-flex flex-column align-items-center mt-5">
          <CircularProgress size={60} />
        </div>
      ) : !hasRecords ? (
        <div className="w-100 d-flex flex-column align-items-center mt-3">
          <img src={inventoryExportImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
          <Box maxWidth={700} width="100%" px={2} className="text-center">
            <h2 className="fst-italic mt-3 text-center">{t('exports.inventoryDescription')}</h2>
            <div
              className="d-flex flex-row align-items-center justify-content-center flex-wrap mt-3"
              style={{ gap: 12 }}
            >
              <Select
                value={idGame}
                onChange={(e) => setIdGame(String(e.target.value))}
                displayEmpty
                style={{ minWidth: 240 }}
              >
                <MenuItem value="">{t('exports.selectGame')}</MenuItem>
                {gameMetaData.map((game) => (
                  <MenuItem key={game.idGame} value={game.idGame}>
                    {game.name}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                onClick={handleCreateCsv}
                disabled={!idGame || isCreating}
              >
                {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
              </Button>
            </div>
          </Box>
        </div>
      ) : (
        <>
          <div>
            <h2 className="mb-4">{t('exports.inventoryTitle')}</h2>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <Select
                  fullWidth
                  value={idGame}
                  onChange={(e) => setIdGame(String(e.target.value))}
                  displayEmpty
                >
                  <MenuItem value="">{t('exports.selectGame')}</MenuItem>
                  {gameMetaData.map((game) => (
                    <MenuItem key={game.idGame} value={game.idGame}>
                      {game.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={handleCreateCsv}
                  disabled={!idGame || isCreating}
                >
                  {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
                </Button>
              </Grid>
            </Grid>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap mt-4 mb-3">
            <span>{t('exports.showExportsCreatedIn')}</span>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
              variant="inline"
              autoOk
              value={filterMonth}
              onChange={(date) => setFilterMonth(date)}
              maxDate={today}
              inputProps={{ style: { padding: '4px 8px', fontSize: 13 } }}
              style={{ width: 170 }}
              PopoverProps={{ classes: { paper: 'exports-month-picker' } }}
            />
            <Button variant="outlined" onClick={handleApplyFilters}>
              {t('exports.applyFilter')}
            </Button>
            {filterMonth && (
              <Button variant="outlined" onClick={handleResetFilter}>
                {t('exports.resetFilter')}
              </Button>
            )}
          </div>

          <div style={{ maxWidth: 700 }}>
            <div>
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Table
                    width={width}
                    height={500}
                    headerHeight={40}
                    rowHeight={40}
                    rowCount={downloads.length}
                    rowGetter={({ index }) => downloads[index]}
                    className={`picking-table`}
                    rowRenderer={rowRenderer}
                    overscanRowCount={5}
                  >
                    <Column
                      width={width * 0.35}
                      label={t('exports.createdAt')}
                      dataKey="created"
                      cellRenderer={({ rowData: a }) => (
                        <div className="text-wrap">{new Date(a.createdAt).toLocaleString()}</div>
                      )}
                    />
                    <Column
                      width={width * 0.35}
                      label={t('exports.game')}
                      dataKey="game"
                      cellRenderer={({ rowData: a }) => {
                        const idGame = a.params?.idGame
                        const game = idGame
                          ? gameMetaData.find((g) => g.idGame.toString() === idGame.toString())
                          : null
                        return <div className="text-wrap">{game ? game.name : '—'}</div>
                      }}
                    />
                    <Column
                      width={width * 0.15}
                      label={t('exports.status')}
                      dataKey="status"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a }) => (
                        <Box textAlign={'center'}>
                          {a.lastErrorAt ? (
                            <i className="fa-light fa-circle-x error-color" />
                          ) : a.exportEndedAt ? (
                            <i className="fa-light fa-circle-check" style={{ color: 'green' }} />
                          ) : (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                      )}
                    />
                    <Column
                      width={width * 0.15}
                      label={t('exports.download')}
                      dataKey="download"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a }) => (
                        <Box textAlign={'center'}>
                          {!a.lastErrorAt && a.exportEndedAt ? (
                            <i
                              className="fa-light fa-arrow-down-to-bracket table-action-icon"
                              onClick={() => handleDownloadCsv(a.id, a.params?.idGame)}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            '—'
                          )}
                        </Box>
                      )}
                    />
                  </Table>
                )}
              </AutoSizer>
            </div>
            {(currentPage > 1 || !isLastPage) && (
              <TablePaginationControls
                currentPage={currentPage}
                setPage={setCurrentPage}
                isLastPage={isLastPage}
                startFromZero={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
